import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { initializeGoogleMaps } from '@/utils/googleMaps';

interface GeocodingResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
}

export const useGoogleMapsGeocoding = () => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const { toast } = useToast();

  const geocodeAddress = useCallback(async (
    address: string, 
    practitionerId?: string
  ): Promise<GeocodingResult | null> => {
    if (!address || address.trim().length < 10) {
      console.log('Address too short for geocoding:', address);
      return null;
    }

    console.log('Starting geocoding for address:', address);
    setIsGeocoding(true);
    
    try {
      // First try the edge function
      console.log('Calling geocode-address edge function...');
      const { data, error } = await supabase.functions.invoke('geocode-address', {
        body: { 
          address: address.trim(),
          practitionerId: practitionerId 
        }
      });
      
      console.log('Edge function response:', { data, error });
      
      if (!error && data?.success && data.latitude && data.longitude) {
        console.log('Edge function geocoding successful:', data);
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          formatted_address: data.formatted_address || address
        };
      }
      
      console.warn('Edge function geocoding failed, trying frontend fallback:', error);
      
      // Fallback to frontend Google Maps API
      const mapsInitialized = await initializeGoogleMaps();
      if (mapsInitialized && window.google && window.google.maps) {
        return new Promise((resolve) => {
          const geocoder = new window.google.maps.Geocoder();
          
          geocoder.geocode({ address: address.trim() }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const location = results[0].geometry.location;
              const result = {
                latitude: location.lat(),
                longitude: location.lng(),
                formatted_address: results[0].formatted_address
              };
              
              console.log('Frontend geocoding successful:', result);
              resolve(result);
            } else {
              console.error('Frontend geocoding failed:', status);
              resolve(null);
            }
          });
        });
      }
      
      // If both methods fail, show error
      console.error('All geocoding methods failed');
      toast({
        title: "Address Verification Failed",
        description: "Unable to verify address, please check the format",
        variant: "destructive",
      });
      
      return null;
      
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    } finally {
      setIsGeocoding(false);
    }
  }, [toast]);

  const updatePractitionerLocation = useCallback(async (
    practitionerId: string,
    latitude: number,
    longitude: number,
    formattedAddress: string
  ) => {
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({
          latitude,
          longitude,
          in_person_address: formattedAddress,
          updated_at: new Date().toISOString()
        })
        .eq('id', practitionerId);

      if (error) throw error;
      
      toast({
        title: "Address Verified",
        description: `Address updated to: ${formattedAddress}`,
      });
      
      return true;
    } catch (error) {
      console.error('Error updating practitioner location:', error);
      toast({
        title: "Update Failed",
        description: "Unable to update address information",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  return {
    geocodeAddress,
    updatePractitionerLocation,
    isGeocoding
  };
};